/**
 * Auth Provider Component
 * Handles session initialization, activity tracking, and session refresh
 */

import { useEffect, useState, useCallback } from 'react';
import { useAuthStore, initAuthChannel, closeAuthChannel, DEV_MODE_ENABLED } from '@/store/authStore';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useSessionRefresh } from '@/hooks/useSessionRefresh';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider - wraps the app and manages authentication state
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const checkSession = useAuthStore((state) => state.checkSession);
  const isLoading = useAuthStore((state) => state.isLoading);
  const sessionChecked = useAuthStore((state) => state.sessionChecked);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const [showIdleWarning, setShowIdleWarning] = useState(false);

  // Initialize auth channel for multi-tab sync
  useEffect(() => {
    initAuthChannel();
    return () => closeAuthChannel();
  }, []);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Handle idle warning
  const handleIdleWarning = useCallback(() => {
    setShowIdleWarning(true);
  }, []);

  // Handle idle timeout
  const handleIdle = useCallback(() => {
    setShowIdleWarning(false);
    // The hook will handle logout
  }, []);

  // Activity tracking with idle timeout.
  // Disabled in dev mode so local sessions don't auto-logout after 30 min idle —
  // developers commonly leave the app open while working in other windows.
  useActivityTracking({
    enabled: isAuthenticated && !DEV_MODE_ENABLED,
    onWarning: handleIdleWarning,
    onIdle: handleIdle,
  });

  // Session refresh before expiry
  useSessionRefresh({
    enabled: isAuthenticated,
  });

  // Show loading state while checking session
  if (!sessionChecked || isLoading) {
    return <AuthLoadingScreen />;
  }

  return (
    <>
      {children}
      
      {/* Idle Warning Modal */}
      {showIdleWarning && (
        <IdleWarningModal
          onContinue={() => setShowIdleWarning(false)}
          onLogout={() => {
            setShowIdleWarning(false);
            useAuthStore.getState().logout();
          }}
        />
      )}
    </>
  );
}

/**
 * Loading screen shown during session check
 * Uses Semaphoric logo for branding consistency.
 */
function AuthLoadingScreen() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fffbf8 0%, #fff7f3 100%)',
        fontFamily: '"Space Grotesk", sans-serif',
      }}
    >
      {/* Semaphoric logo */}
      <div
        style={{
          width: 64,
          height: 64,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src="/logo-icon.svg" alt="Semaphoric" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>

      {/* Spinner */}
      <div
        style={{
          width: 32,
          height: 32,
          border: '3px solid rgba(255, 107, 53, 0.2)',
          borderTopColor: '#ff6b35',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: 16,
        }}
      />

      <p style={{ color: '#666', fontSize: 14 }}>Loading...</p>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Idle warning modal
 */
interface IdleWarningModalProps {
  onContinue: () => void;
  onLogout: () => void;
}

function IdleWarningModal({ onContinue, onLogout }: IdleWarningModalProps) {
  const [countdown, setCountdown] = useState(300); // 5 minutes

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onLogout]);

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: '"Space Grotesk", sans-serif',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          padding: 32,
          maxWidth: 400,
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        }}
      >
        {/* Warning Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 20px',
            background: 'rgba(255, 107, 53, 0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ff6b35" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600, color: '#1a1a1a' }}>
          Session Timeout Warning
        </h2>
        
        <p style={{ margin: '0 0 24px', color: '#666', fontSize: 14, lineHeight: 1.5 }}>
          You've been inactive for a while. For security, you'll be logged out in:
        </p>

        {/* Countdown */}
        <div
          style={{
            fontSize: 36,
            fontWeight: 700,
            color: countdown < 60 ? '#dc3545' : '#ff6b35',
            marginBottom: 24,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {minutes}:{seconds.toString().padStart(2, '0')}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onLogout}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: 'transparent',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              color: '#666',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Log Out
          </button>
          <button
            onClick={onContinue}
            style={{
              flex: 1,
              padding: '12px 20px',
              background: '#ff6b35',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuthProvider;

